import { useSyncExternalStore } from 'react'

import {
  applyTheme,
  readStoredTheme,
  resolveTheme,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type Theme,
} from './theme'

/**
 * Tema durumu — modül düzeyinde küçük bir mağaza, `Context` değil.
 *
 * Tema tek bir sınıfa dönüşüyor ve onu okuyan tek şey anahtarın kendisi; araya bir sağlayıcı
 * koymak, tek tüketicisi olan bir değeri bütün ağaca dolaştırmak olurdu. `useSyncExternalStore`
 * ayrıca sunucudan gelen HTML ile ilk çizimin uyuşmasını da garanti eder.
 */

let current: Theme = 'system'
let listeners: (() => void)[] = []

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void): () => void {
  listeners = [...listeners, listener]

  return () => {
    listeners = listeners.filter((entry) => entry !== listener)
  }
}

export function setTheme(theme: Theme): void {
  current = theme
  applyTheme(theme)

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Yazılamadıysa tercih bu sekmede geçerli kalır ama kalıcı olmaz. Sessiz geçmek doğru:
    // kullanıcının istediği şey (tema değişsin) zaten oldu.
  }

  emit()
}

/**
 * Uygulama açılırken bir kez çağrılır.
 *
 * Sınıfı `index.html`'deki satır içi betik zaten yazdı — buradaki iş, o kararı React'in
 * durumuna **almak** ve işletim sistemi tercihi oturum sırasında değişirse takip etmek. İkincisi
 * olmasa, "Sistem" seçili bir kullanıcı gece moduna geçtiğinde uygulama sayfayı yenileyene kadar
 * eski temada kalırdı.
 */
export function initTheme(): () => void {
  current = readStoredTheme()
  applyTheme(current)
  emit()

  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const onSystemChange = (): void => {
    if (current === 'system') {
      applyTheme(current)
      emit()
    }
  }

  media.addEventListener('change', onSystemChange)

  return () => {
    media.removeEventListener('change', onSystemChange)
  }
}

function getSnapshot(): Theme {
  return current
}

export function useTheme(): { theme: Theme; resolved: ResolvedTheme; setTheme: typeof setTheme } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  return { theme, resolved: resolveTheme(theme), setTheme }
}
